// CameraUpdater.tsx
import type React from "react";

import {useFrame} from "@react-three/fiber";
import {OrthographicCamera, Vector2} from "three";

import useMyCamera from "./useMyCamera";

export const CameraUpdater: React.FC = () => {
  const {
    currentPosition,
    targetPosition,
    cameraVelocity,
    currentZoom,
    targetZoom,
    zoomVelocity,
  } = useMyCamera();

  // Tweak these constants to adjust smoothness.
  const movementDamping = 0.5; // Higher value preserves more momentum.
  const movementAcceleration = 4.0; // Larger values mean faster acceleration.

  const zoomDamping = 0.5;
  const zoomAcceleration = 4.0;

  useFrame(({camera}, delta) => {
    // Smoothly update position:
    // Calculate the difference between the target and current positions:
    const posDiff = new Vector2().subVectors(
      targetPosition.current,
      currentPosition.current,
    );
    posDiff.multiplyScalar(delta * movementAcceleration);
    // Update velocity with the "impulse" then apply damping.
    cameraVelocity.current.add(posDiff).multiplyScalar(movementDamping);
    // Integrate velocity to update current position.
    currentPosition.current.add(cameraVelocity.current);
    // Update camera (preserving the z-value).
    camera.position.set(
      currentPosition.current.x,
      currentPosition.current.y,
      camera.position.z,
    );

    // Smoothly update zoom for orthographic cameras:
    if (camera instanceof OrthographicCamera) {
      // Work in logarithmic space for perceptual consistency.
      const logCurrentZoom = Math.log(currentZoom.current);
      const logTargetZoom = Math.log(targetZoom.current);
      const zoomError = logTargetZoom - logCurrentZoom;
      const zoomImpulse = zoomError * delta * zoomAcceleration;

      zoomVelocity.current += zoomImpulse;
      zoomVelocity.current *= zoomDamping;

      const newLogZoom = logCurrentZoom + zoomVelocity.current;
      const newZoom = Math.exp(newLogZoom);

      currentZoom.current = newZoom; // update current zoom in context
      camera.zoom = newZoom;
    }

    camera.updateProjectionMatrix();
  });

  return null;
};

export default CameraUpdater;
